import { log } from '../../utils/consoleLogger';
import * as THREE from 'three';

/**
 * Get CSS variable value with fallback
 */
export const getCssVariableFov = (variableName, fallback) => {
  if (typeof window === 'undefined') return fallback;

  const configuratorRoot = document.getElementById('root');
  const rootStyles = window.getComputedStyle(document.documentElement);
  const configuratorStyles = configuratorRoot ? window.getComputedStyle(configuratorRoot) : null;

  const rawValue =
    configuratorStyles?.getPropertyValue(variableName)?.trim() ||
    rootStyles.getPropertyValue(variableName)?.trim();

  const parsedValue = parseFloat(rawValue);

  if (Number.isFinite(parsedValue)) {
    return parsedValue;
  }

  console.warn(`[FOV] CSS variable ${variableName} missing or invalid ("${rawValue}"). Using fallback ${fallback}.`);
  return fallback;
};

/**
 * Get root URL of the current page
 */
export const getRootUrl = () => {
  const { protocol, host } = window.location;
  return `${protocol}//${host}`;
};

/**
 * Calculate camera distance to fit object in view
 */
export const calculateCameraDistance = (modelRef, camera) => {
  if (!modelRef.current) return null;

  const box = new THREE.Box3().setFromObject(modelRef.current);
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const fov = camera.fov * (Math.PI / 180);
  
  return maxDim / (2 * Math.tan(fov / 2));
};

/**
 * Initialize default params object
 */
export const initializeParams = (productSettings, isAndroid) => {
  return {
    //Canvas
    canvas_width: window.innerWidth,
    canvas_height: window.innerHeight,
    zoomClose: 0.25,
    zoomFar: 1,
    minZoomClose: 0.1,
    maxZoomClose: 2,
    maxZoomFar: 20,
    maxZoomFactor: 2,
    zoomOnPartMultiplier: 0.55,
    zoomMultiplier: 0.5,
    cameraOffset_desktop: 2,
    cameraOffset_mobile: 2,
    cameraFOV_desktop: 45,
    cameraFOV_mobile: 45,
    smallObjectScaleUp: 1,

    //Shadows
    shadowmapsize: 2048,
    shadowblurradius: 10,
    shadowblursamples: 15,
    shadowbias: -0.001,
    shadowNear: 1.5,
    shadowFar: 100,

    //Lights
    lightAngle: Math.PI / 5,
    lightRadius: 1.5,
    lightheight: 2.5,
    spotpenumbra: 0.5,
    spotdecay: 2,
    amblight_colour: 0xffffff,
    amblight_intensity: 0.15,
    dirlightIntensity: 0.8,
    lightMapIntensity: 3.5,
    bakedAmblight_colour: 0xffffff,
    bakedAmblight_intensity: 5,
    lights_colour: 0xffffff,
    lights_intensity: 4,
    spotLightOn: false,
    spot_intensity: 10,
    showLabelsOn: false,

    //Environment
    floor_colour: 0xffffff,
    background_colour: 0xffffff,
    environmentIntensity: 1.75,

    //Controls
    light_position: 7,
    helpers: false,
    enablePan: false,
    fullAngle: false,

    //Lines
    line_model_offset: 0.02,
    line_floor_offset: 0.01,
    arrow_head_offset: 0.02,
    line_colour: productSettings?.labelColour || (productSettings?.sceneBackgroundColour === "black" ? "#ffffff" : "#000000"),
    line_width_desktop: 4,
    line_width_mobile: 2,
    line_opacity: 0.5,
    cone_opacity: 0.7,
    lineOffset: 1.5,

    //Labels
    labelColour: productSettings?.labelColour ? parseInt(productSettings.labelColour.replace('#', '0x')) : (productSettings?.sceneBackgroundColour === "black" ? 0xffffff : 0x000000),

    //Rendering Effects
    bloom_threshold: 0,
    bloom_strength: 1,
    bloom_radius: 0.5,
    saoBias: 0.5,
    saoIntensity: 0.07,
    saoScale: 0.8,
    saoKernelRadius: 1,
    saoBlur: true,
    saoBlurRadius: 10,
    saoBlurStdDev: 5,
    saoBlurDepthCutoff: 0.01,

    //OS
    isAndroid: isAndroid,
  };
};

/**
 * Update params based on product tags
 */
export const updateParamsForProductTags = (params, productTags, productSettings) => {
  params.line_colour = productSettings.dimLineColour 
    ? parseInt(productSettings.dimLineColour.replace('#', '0x'))
    : (productSettings.sceneBackgroundColour === "black" ? 0xffffff : 0x000000);
  
  params.labelColour = productSettings.labelColour ||
    (productSettings.sceneBackgroundColour === "black" ? "#ffffff" : "#000000");

  if (productTags.includes("jewellery")) {
    params.modelScale = 10;
    params.environmentIntensity = 1;
    params.enablePan = true;
    params.fullAngle = true;
  } else {
    if (productTags.includes("fabrics")) {
      log("fabrics");
      params.environmentIntensity = 0;
    }
    if (productTags.includes("reflective")) {
      log("reflective");
      params.environmentIntensity = 1;
    }
    if (productTags.includes("glass")) {
      log("glass");
      params.environmentIntensity = 1;
      params.enablePan = true;
      params.fullAngle = true;
    }
    if (productTags.includes("freeCam")) {
      log("free cam");
      params.enablePan = true;
      params.fullAngle = true;
    }
    if (productTags.includes("smallObject")) {
      params.environmentIntensity = 0.75;
    }
  }
  
  if (productSettings.fullAngle) {
    params.fullAngle = productSettings.fullAngle;
  }
};
